/**
 * فاکتورهای لغوشده — بایگانیِ جدا از فهرستِ فاکتورها.
 *
 * چرا جداست: لغوشده‌ها در فهرستِ فاکتورها جمع‌ها و گزارشِ مانده را خراب می‌کردند.
 * اینجا دیده، بازگردانده (به پیش‌نویس) یا برای همیشه پاک می‌شوند.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Table, Button, Space, Tag, message, Popconfirm, Input, Row, Col, Statistic, Typography, Empty } from "antd";
import Alert from "../../components/GxAlert";
import { UndoOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  scGetCancelledEvents, scRestoreEvent, scBulkDeleteEvents, scBulkRestoreEvents,
} from "../../api/client";
import { eventLabel, eventColor } from "./scConstants";
import { toJalali } from "../../utils/dateUtils";
import { useAuth } from "../../context/AuthContext";
import { faNum } from "../../utils/faNumberFormat";

const { Text } = Typography;

const kg = (v) => (v == null ? "—" : `${faNum(Number(v), { maximumFractionDigits: 1 })}`);
const money = (v) => (v == null ? "—" : faNum(Math.round(v)));

export default function CancelledEventsPage() {
  const { can } = useAuth();
  const canEdit = can("supply_chain.create");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    scGetCancelledEvents()
      .then((r) => { setRows(r.data || []); setSelected([]); })
      .catch(() => message.error("خطا در دریافت فاکتورهای لغوشده"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const n = q.trim();
    return rows.filter((r) =>
      [r.event_code, r.item_name, r.external_document_number, r.notes,
       r.source_party_name, r.destination_party_name]
        .some((v) => String(v || "").includes(n)));
  }, [rows, q]);

  const restoreOne = async (id) => {
    try {
      const r = await scRestoreEvent(id);
      message.success(r.data.message);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "بازگردانی ناموفق بود");
    }
  };

  const bulk = async (fn, label) => {
    setLoading(true);
    try {
      const r = await fn(selected);
      message.success(r.data.message);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      message.error(typeof d === "string" ? d : d?.message || `${label} ناموفق بود`);
      setLoading(false);
    }
  };

  const columns = [
    { title: "کد سند", dataIndex: "event_code", width: 150,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: "تاریخ", dataIndex: "event_date", width: 105,
      render: (v) => (v ? toJalali(v) : "—") },
    { title: "نوع", dataIndex: "event_type", width: 165,
      render: (v) => <Tag color={eventColor(v)}>{eventLabel(v)}</Tag> },
    { title: "از", dataIndex: "source_party_name", width: 130 },
    { title: "به", dataIndex: "destination_party_name", width: 130 },
    { title: "کالا", dataIndex: "item_name", width: 130 },
    { title: "وزن (kg)", dataIndex: "weight_kg", width: 110, align: "left", render: kg },
    { title: "مبلغ (ریال)", dataIndex: "total_amount", width: 140, align: "left", render: money },
    { title: "شماره سند", dataIndex: "external_document_number", width: 130 },
    { title: "توضیح", dataIndex: "notes", ellipsis: true },
    ...(canEdit ? [{
      title: "عملیات", width: 120, align: "center", fixed: "left",
      render: (_, r) => (
        <Popconfirm title="به فهرستِ فاکتورها برگردد؟"
          description="به‌صورت «پیش‌نویس» برمی‌گردد و برای ثبت در دفتر باید دوباره تأیید شود."
          okText="بازگردان" cancelText="انصراف" onConfirm={() => restoreOne(r.id)}>
          <Button size="small" icon={<UndoOutlined />}>بازگردانی</Button>
        </Popconfirm>
      ),
    }] : []),
  ];

  const totalKg = filtered.reduce((s, r) => s + (Number(r.weight_kg) || 0), 0);
  const totalAmount = filtered.reduce((s, r) => s + (Number(r.total_amount) || 0), 0);

  return (
    <Card
      title="فاکتورهای لغوشده"
      extra={<Space>
        <Input.Search allowClear placeholder="جستجو در کد، کالا، سند…" style={{ width: 220 }}
          value={q} onChange={(e) => setQ(e.target.value)} />
        <Button icon={<ReloadOutlined />} onClick={load}>بازخوانی</Button>
      </Space>}
    >
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="این فاکتورها از فهرستِ فاکتورها کنار گذاشته شده‌اند"
        description="در جمع‌ها، گزارشِ مانده و خروجی‌ها شمرده نمی‌شوند. با «بازگردانی» به‌صورت پیش‌نویس به فهرستِ فاکتورها برمی‌گردند و برای ثبت در دفتر امانی باید دوباره تأیید شوند." />

      <Row gutter={12} style={{ marginBottom: 12 }} key={`${filtered.length}`}>
        <Col xs={8}><Card size="small"><Statistic title="تعداد" value={filtered.length} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="جمع وزن (kg)" value={kg(totalKg)} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="جمع مبلغ (ریال)" value={money(totalAmount)} /></Card></Col>
      </Row>

      {canEdit && selected.length > 0 && (
        <Space style={{ marginBottom: 12 }} wrap>
          <Tag color="blue">{selected.length} ردیف انتخاب شده</Tag>
          <Popconfirm title={`${selected.length} فاکتور به فهرست برگردد؟`} okText="بازگردان"
            cancelText="انصراف" onConfirm={() => bulk(scBulkRestoreEvents, "بازگردانی")}>
            <Button icon={<UndoOutlined />}>بازگردانیِ انتخاب‌شده‌ها</Button>
          </Popconfirm>
          <Popconfirm title={`${selected.length} فاکتور برای همیشه حذف شود؟`}
            description="این کار برگشت‌پذیر نیست."
            okText="حذف" cancelText="انصراف" okButtonProps={{ danger: true }}
            onConfirm={() => bulk(scBulkDeleteEvents, "حذف")}>
            <Button danger icon={<DeleteOutlined />}>حذفِ انتخاب‌شده‌ها</Button>
          </Popconfirm>
        </Space>
      )}

      <Table
        size="small" rowKey="id" columns={columns} dataSource={filtered} loading={loading}
        scroll={{ x: "max-content" }}
        pagination={{ defaultPageSize: 25, showSizeChanger: true }}
        rowSelection={canEdit ? {
          selectedRowKeys: selected,
          onChange: setSelected,
        } : undefined}
        locale={{ emptyText: <Empty description="فاکتورِ لغوشده‌ای نیست" /> }}
      />
    </Card>
  );
}
